// Reads the Swift app's project and fragment documents with its key names, defaults and type rules
// (SuiteModel: ProjectDocument.swift, FragmentDocument.swift, ModelTypes.swift, StoryModel.swift, WritingStats.swift,
// SettingsJSON.swift). Started from the W0 spike's schema.ts. What would throw in Swift is recorded as an issue with
// its path and read as the default instead, so one bad field never loses a project.
import type * as A from '@automerge/automerge';
import type {
  ArrangementRole, Comment, EntityKind, FieldType, FragmentKind, MetadataValue, ProjectType,
  AttachmentRef } from '../contract.ts';
import { joinedAt, JoinedMap } from '../automerge/joined.ts';
import { ROOT, SwiftReader, type ObjID } from '../automerge/swiftDoc.ts';
import { swiftCompare } from './order.ts';
import type { ScriptFormat } from '../scriptFormat.ts';
import { emptyMapContent, readMapContent, type MapContent } from '../maps/map.ts';

export interface Issue { path: string; error: string }

class Ctx {
  issues: Issue[] = [];
  constructor(readonly r: SwiftReader) {}
  try<T>(path: string, fn: () => T, fallback: T): T {
    try {
      return fn();
    } catch (e) {
      this.issues.push({ path, error: (e as Error).message });
      return fallback;
    }
  }
}

const FRAGMENT_KINDS: readonly FragmentKind[] = ['prose', 'screenplay', 'note', 'research', 'front-matter', 'map'];
const PROJECT_TYPES: readonly ProjectType[] = ['manuscript', 'screenplay', 'journal'];
const ROLES: readonly ArrangementRole[] = ['manuscript', 'reference', 'publishing', 'collection', 'alternate', 'trash'];
const FIELD_TYPES: readonly FieldType[] = ['text', 'enum', 'date', 'number', 'boolean', 'story-time', 'entity-ref', 'tags'];
const ENTITY_KINDS: readonly EntityKind[] = ['character', 'place', 'object', 'motif'];
const TRIGGERS = ['manual', 'compile', 'before-suggestions', 'before-sync', 'daily', 'before-import', 'imported'] as const;
export type CheckpointTrigger = (typeof TRIGGERS)[number];

const oneOf = <T extends string>(all: readonly T[], raw: string | undefined, fallback: T): T =>
  raw !== undefined && (all as readonly string[]).includes(raw) ? (raw as T) : fallback;

// MARK: Types (ModelTypes.swift, as stored)

export interface ArrangementNode {
  id: string;
  fragmentID?: string;
  /** Title for pure folders; fragment-backed nodes use the fragment's title. */
  title?: string;
  isFolder: boolean;
  parentID?: string;
  position: string;
  board?: { x: number; y: number };
}

export interface Arrangement { id: string; name: string; role: ArrangementRole; order: number; nodes: Map<string, ArrangementNode> }

export interface MetadataField {
  id: string; name: string; type: FieldType; options: { id: string; name: string; color?: string }[]; order: number;
  /** Removed by the writer: left out of what they see, its values kept (`MetadataField.hidden`). Present only when set. */
  hidden?: true;
}

export interface StoryEntity {
  id: string; kind: EntityKind; name: string; aliases: string[]; notes: string; birth?: Date;
  latitude?: number; longitude?: number; timeZoneID?: string; studyID?: string; color?: string; role?: string;
}

export interface CheckpointRecord {
  id: string; name: string; createdAt: Date; createdBy: string; trigger: CheckpointTrigger; epoch: number;
  projectHeads: string; fragmentHeads: Record<string, string>;
}

export interface FragmentEntry { id: string; kind: FragmentKind; trashed: boolean; createdAt: Date; source?: string }

export interface WritingTargets { manuscriptWords?: number; sessionWords?: number; countAllText: boolean }

export interface ProjectRecord {
  id: string; title: string; createdAt: Date; source?: string; epoch: number;
  locale: string; projectType: ProjectType;
  /** `ProjectDocument.scriptFormat`: 'screen' unless a screenplay is a play (`scriptFormat.ts`). */
  scriptFormat: ScriptFormat;
  series: string | null;
  targets: WritingTargets;
  /** Every settings entry: scalars as read, text objects as strings, maps recursed (JSON strings are left as strings;
   *  `settingJSON` parses one as `decodeJSON` does). */
  settings: Record<string, unknown>;
  fragments: Map<string, FragmentEntry>;
  fields: MetadataField[];
  arrangements: Arrangement[];
  entities: StoryEntity[];
  checkpoints: CheckpointRecord[];
  issues: Issue[];
}

export interface Suggestion {
  id: string; author: string; authorKind: 'human' | 'agent'; anchorStart: number; anchorEnd: number; anchorHeads: string;
  quote: string; proposedText: string; note: string; status: 'open' | 'accepted' | 'rejected'; createdAt: Date;
}

export interface FragmentRecord {
  id: string; projectID: string; epoch: number; kind: FragmentKind; title: string; synopsis: string; text: string; notes: string;
  includeInCompile: boolean; createdAt: Date | null; modifiedAt: Date | null;
  /** Locked against accidental change (`Locks.swift`); a folder's lock reaches its subtree (`effectiveLocks`). */
  locked: boolean;
  metadata: Record<string, MetadataValue>;
  /** The other text values a field holds at once, where two devices changed it before syncing (`metadata` has the one
   *  Automerge shows): what a tags field reads as well (`fragmentTags`). Sorted, so every device reads them alike. */
  metadataConflicts: Record<string, string[]>;
  comments: Comment[]; synopsisComments: Comment[]; suggestions: Suggestion[];
  /** `FragmentDocument.attachments()`: the `attachments` list of `{sha256, filename, mimeType}` maps, in order. */
  attachments: AttachmentRef[];
  /** `"<day> <device> +|-"` → that device's running total for the day. */
  wordLog: Record<string, number>;
  /** Presence the writer set, by entity (`explicitPresence`). */
  presence: Record<string, string>;
  /** A map's notes, lines, regions and strokes (`mapContent()`); only when the kind is `map`. */
  map?: MapContent;
  issues: Issue[];
}

/** Foundation's `Date.distantPast`, which the Swift reads a missing date as. */
export const DISTANT_PAST = -62135769600000;
const dateOrNull = (d: Date | undefined) => (!d || d.getTime() <= DISTANT_PAST ? null : d);

// MARK: Fragments

/** `MetadataValue(scalar:)`: scalars only; a text object (or a counter) is left out. */
function metadataValue(datatype: string, value: unknown): MetadataValue | undefined {
  switch (datatype) {
    case 'str': return { type: 'text', value: value as string };
    case 'f64': case 'int': case 'uint': return { type: 'number', value: Number(value) };
    case 'boolean': return { type: 'boolean', value: value as boolean };
    case 'timestamp': return { type: 'date', value: value as Date };
    default: return undefined;
  }
}

/** `FragmentDocument.attachments()`: entries without a `sha256` are left out, as the Swift leaves them. */
function readAttachments(c: Ctx): AttachmentRef[] {
  const r = c.r;
  const list = c.try('attachments', () => r.list(ROOT, 'attachments'), undefined);
  if (!list) return [];
  const out: AttachmentRef[] = [];
  for (const [t, obj] of r.values(list)) {
    if (t !== 'map') continue;
    const o = obj as ObjID;
    const sha256 = r.string(o, 'sha256');
    if (sha256) out.push({ sha256, filename: r.string(o, 'filename') ?? '', mimeType: r.string(o, 'mimeType') ?? 'application/octet-stream' });
  }
  return out;
}

function readComments(c: Ctx, key: string): Comment[] {
  const r = c.r;
  // `synopsisComments` is made on first use, so two devices may each have made one (`JoinedMap`).
  const all = c.try(key, () => joinedAt(r, ROOT, key), undefined);
  if (!all) return [];
  const out: Comment[] = [];
  for (const [id, [t, obj]] of all.entries()) {
    if (t !== 'map') continue;
    const o = obj as ObjID;
    c.try(`${key}/${id}`, () => {
      out.push({
        id, author: r.string(o, 'author') ?? '', authorKind: oneOf(['human', 'agent'], r.string(o, 'authorKind'), 'human'),
        body: r.string(o, 'body') ?? '', quote: r.string(o, 'quote') ?? '',
        anchorStart: r.uint(o, 'anchorStart') ?? 0, anchorEnd: r.uint(o, 'anchorEnd') ?? 0,
        anchorHeads: r.string(o, 'anchorHeads') ?? '', parentId: r.string(o, 'parent') ?? null,
        status: oneOf(['open', 'resolved'], r.string(o, 'status'), 'open'),
        createdAt: r.date(o, 'createdAt') ?? new Date(DISTANT_PAST),
        category: r.string(o, 'category') ?? null,
      });
    }, undefined);
  }
  // `sorted { $0.createdAt < $1.createdAt }` (stable in practice; ids break ties here).
  return out.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime() || swiftCompare(a.id, b.id));
}

export function readFragment(doc: A.Doc<unknown>, heads?: string[]): FragmentRecord {
  const r = new SwiftReader(doc, heads);
  const c = new Ctx(r);
  const s = (key: string, fallback = '') => c.try(key, () => r.string(ROOT, key) ?? fallback, fallback);

  const metadata: Record<string, MetadataValue> = {};
  const metadataConflicts: Record<string, string[]> = {};
  c.try('metadata', () => {
    const obj = r.map(ROOT, 'metadata');
    if (!obj) return;
    for (const [k, [t, v]] of r.entries(obj)) {
      const m = metadataValue(t, v);
      if (m) metadata[k] = m;
      if (t !== 'str') continue;
      const all = (r.backend.getAll(obj, k, r.heads) as unknown as [string, unknown, string][]) ?? [];
      if (all.length < 2) continue;
      const others = [...new Set(all.filter((x) => x[0] === 'str' && x[1] !== v).map((x) => x[1] as string))].sort(swiftCompare);
      if (others.length) metadataConflicts[k] = others;
    }
  }, undefined);

  const suggestions: Suggestion[] = [];
  c.try('suggestions', () => {
    const all = r.map(ROOT, 'suggestions');
    if (!all) return;
    for (const [id, [t, o]] of r.entries(all)) {
      if (t !== 'map') continue;
      const obj = o as ObjID;
      suggestions.push({
        id, author: r.string(obj, 'author') ?? '', authorKind: oneOf(['human', 'agent'], r.string(obj, 'authorKind'), 'human'),
        anchorStart: r.uint(obj, 'anchorStart') ?? 0, anchorEnd: r.uint(obj, 'anchorEnd') ?? 0,
        anchorHeads: r.string(obj, 'anchorHeads') ?? '', quote: r.string(obj, 'quote') ?? '',
        proposedText: r.string(obj, 'proposedText') ?? '', note: r.string(obj, 'note') ?? '',
        status: oneOf(['open', 'accepted', 'rejected'], r.string(obj, 'status'), 'open'),
        createdAt: r.date(obj, 'createdAt') ?? new Date(DISTANT_PAST),
      });
    }
    suggestions.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }, undefined);

  // `dailyWords`: `doc.int(log, key)` for each key; a wrong type throws there, so it's an issue here.
  const wordLog: Record<string, number> = {};
  c.try('wordLog', () => {
    const log = joinedAt(r, ROOT, 'wordLog');
    for (const key of log.keys()) { const n = log.int(key); if (n !== undefined) wordLog[key] = n; }
  }, undefined);

  const presence: Record<string, string> = {};
  c.try('presence', () => {
    for (const [id, [t, v]] of joinedAt(r, ROOT, 'presence').entries()) if (t === 'str') presence[id] = v as string;
  }, undefined);

  const kind = oneOf(FRAGMENT_KINDS, s('kind', 'prose'), 'prose');
  const map = kind === 'map' ? c.try('map', () => readMapContent(r), emptyMapContent()) : undefined;

  return {
    id: c.try('id', () => r.requiredString(ROOT, 'id'), ''),
    projectID: c.try('projectId', () => r.requiredString(ROOT, 'projectId'), ''),
    epoch: c.try('epoch', () => r.int(ROOT, 'epoch') ?? 0, 0),
    kind,
    title: s('title'), synopsis: s('synopsis'), text: s('text'), notes: s('notes'),
    includeInCompile: c.try('includeInCompile', () => r.bool(ROOT, 'includeInCompile') ?? true, true),
    locked: c.try('locked', () => r.bool(ROOT, 'locked') ?? false, false),
    createdAt: dateOrNull(c.try('createdAt', () => r.date(ROOT, 'createdAt'), undefined)),
    modifiedAt: dateOrNull(c.try('modifiedAt', () => r.date(ROOT, 'modifiedAt'), undefined)),
    metadata, metadataConflicts,
    comments: readComments(c, 'comments'),
    attachments: readAttachments(c),
    synopsisComments: readComments(c, 'synopsisComments'),
    suggestions, wordLog, presence,
    ...(map ? { map } : {}),
    issues: c.issues,
  };
}

// MARK: The project

/** A setting as a plain value. A map is every map at its key, joined (`JoinedMap`): the lists kept in the settings
 *  (the glossary, collections, pronunciations…) are each made on first use, so two devices may each have made one. */
function plainSetting(r: SwiftReader, full: [string, unknown], maps?: JoinedMap): unknown {
  const [t, v] = full;
  if (t === 'map') {
    const joined = maps ?? new JoinedMap(r, [v as ObjID]);
    const out: Record<string, unknown> = {};
    for (const [k, f] of joined.entries()) out[k] = plainSetting(r, f, joined.joined(k));
    return out;
  }
  if (t === 'list') return r.values(v as ObjID).map((f) => plainSetting(r, f));
  if (t === 'text') return r.text(v as ObjID);
  return typeof v === 'bigint' ? Number(v) : v;
}

/** `Series.normalized`: trimmed, inner runs of whitespace made one space; null when blank. */
export function normalizedSeries(name: string): string | null {
  const words = name.split(/\p{White_Space}+/u).filter((w) => w !== '');
  return words.length === 0 ? null : words.join(' ');
}

/** `split(separator: "\n")` as Swift does it: "\r\n" is one Character, so it doesn't split; empties omitted. */
const splitAliases = (s: string) => s.split(/(?<!\r)\n/).filter((a) => a !== '');

export function readProject(doc: A.Doc<unknown>, heads?: string[]): ProjectRecord {
  const r = new SwiftReader(doc, heads);
  const c = new Ctx(r);

  const settingsObj = c.try('settings', () => r.map(ROOT, 'settings'), undefined);
  const settings: Record<string, unknown> = {};
  const settingsMaps = joinedAt(r, ROOT, 'settings');
  if (settingsObj) for (const [k, f] of r.entries(settingsObj)) settings[k] = c.try(`settings/${k}`, () => plainSetting(r, f, settingsMaps.joined(k)), undefined);
  const setting = <T>(key: string, read: (obj: ObjID) => T | undefined, fallback: T): T =>
    settingsObj ? c.try(`settings/${key}`, () => read(settingsObj) ?? fallback, fallback) : fallback;

  const targets: WritingTargets = settingsObj ? c.try('settings/targets', () => {
    const t = settingsMaps.joined('targets');
    const manuscriptWords = t.int('manuscriptWords'), sessionWords = t.int('sessionWords');
    return {
      ...(manuscriptWords !== undefined ? { manuscriptWords } : {}), ...(sessionWords !== undefined ? { sessionWords } : {}),
      countAllText: t.bool('countAllText') ?? false,
    };
  }, { countAllText: false }) : { countAllText: false };

  const fragments = new Map<string, FragmentEntry>();
  c.try('fragments', () => {
    const reg = r.map(ROOT, 'fragments');
    if (!reg) return;
    for (const [id, [t, o]] of r.entries(reg)) {
      if (t !== 'map') continue;
      const obj = o as ObjID;
      c.try(`fragments/${id}`, () => fragments.set(id, {
        id, kind: oneOf(FRAGMENT_KINDS, r.string(obj, 'kind'), 'prose'), trashed: r.bool(obj, 'trashed') ?? false,
        createdAt: r.date(obj, 'createdAt') ?? new Date(DISTANT_PAST), source: r.string(obj, 'source'),
      }), undefined);
    }
  }, undefined);

  const fields: MetadataField[] = c.try('metadataSchema', () => {
    const schema = r.map(ROOT, 'metadataSchema');
    if (!schema) return [];
    const out: MetadataField[] = [];
    for (const [id, [t, o]] of r.entries(schema)) {
      if (t !== 'map') continue;
      const obj = o as ObjID;
      const options: MetadataField['options'] = [];
      const seen = new Set<string>();
      const list = r.list(obj, 'options');
      if (list) for (const [ot, opt] of r.values(list)) {
        if (ot !== 'map') continue;
        // Two devices moving one option leave it in the list twice: the first is the one that counts.
        const optionID = r.requiredString(opt as ObjID, 'id');
        if (seen.has(optionID)) continue;
        seen.add(optionID);
        const color = r.string(opt as ObjID, 'color');
        options.push({ id: optionID, name: r.string(opt as ObjID, 'name') ?? '', ...(color !== undefined ? { color } : {}) });
      }
      out.push({
        id, name: r.string(obj, 'name') ?? id, type: oneOf(FIELD_TYPES, r.string(obj, 'type'), 'text'), options, order: r.double(obj, 'order') ?? 0,
        ...(r.bool(obj, 'hidden') ? { hidden: true as const } : {}),
      });
    }
    return out.sort((a, b) => a.order - b.order || swiftCompare(a.id, b.id));
  }, []);

  const arrangements: Arrangement[] = c.try('arrangements', () => {
    // An arrangement with a fixed id (the Trash, Research & Notes, Publishing) is made on first use, so two devices
    // may each have made one, each with its own `nodes`: they are one arrangement (`arrangementMaps`).
    const all = joinedAt(r, ROOT, 'arrangements');
    const out: Arrangement[] = [];
    for (const id of all.keys()) {
      const obj = all.joined(id);
      if (obj.isEmpty) continue;
      const nodes = new Map<string, ArrangementNode>();
      const nodeMap = obj.joined('nodes');
      for (const [nid, [nt, n]] of nodeMap.entries()) {
        if (nt !== 'map') continue;
        const no = n as ObjID;
        const x = r.double(no, 'boardX');
        const y = x === undefined ? undefined : r.double(no, 'boardY');
        const fragmentID = r.string(no, 'fragment'), title = r.string(no, 'title'), parentID = r.string(no, 'parent');
        nodes.set(nid, {
          id: nid, isFolder: r.bool(no, 'folder') ?? false, position: r.string(no, 'position') ?? '',
          ...(fragmentID !== undefined ? { fragmentID } : {}), ...(title !== undefined ? { title } : {}),
          ...(parentID !== undefined ? { parentID } : {}), ...(x !== undefined && y !== undefined ? { board: { x, y } } : {}),
        });
      }
      out.push({ id, name: obj.string('name') ?? 'Untitled', role: oneOf(ROLES, obj.string('role'), 'alternate'), order: obj.double('order') ?? 0, nodes });
    }
    return out.sort((a, b) => a.order - b.order || swiftCompare(a.id, b.id));
  }, []);

  const entities: StoryEntity[] = c.try('entities', () => {
    const all = r.map(ROOT, 'entities');
    if (!all) return [];
    const out: StoryEntity[] = [];
    for (const [id, [t, o]] of r.entries(all)) {
      if (t !== 'map') continue;
      const obj = o as ObjID;
      const kind = r.string(obj, 'kind');
      if (!kind || !(ENTITY_KINDS as readonly string[]).includes(kind)) continue;
      const optional = {
        birth: r.date(obj, 'birth'), latitude: r.double(obj, 'latitude'), longitude: r.double(obj, 'longitude'),
        timeZoneID: r.string(obj, 'timeZone'), studyID: r.string(obj, 'study'), color: r.string(obj, 'color'), role: r.string(obj, 'role'),
      };
      out.push({
        id, kind: kind as EntityKind, name: r.string(obj, 'name') ?? '', aliases: splitAliases(r.string(obj, 'aliases') ?? ''),
        notes: r.string(obj, 'notes') ?? '',
        ...Object.fromEntries(Object.entries(optional).filter(([, v]) => v !== undefined)),
      });
    }
    // (kind, name.localizedLowercase): toLocaleLowerCase is the nearest JS equivalent.
    return out.sort((a, b) => swiftCompare(a.kind, b.kind) || swiftCompare(a.name.toLocaleLowerCase(), b.name.toLocaleLowerCase()));
  }, []);

  const checkpoints: CheckpointRecord[] = c.try('checkpoints', () => {
    const all = r.map(ROOT, 'checkpoints');
    if (!all) return [];
    const out: CheckpointRecord[] = [];
    for (const [id, [t, o]] of r.entries(all)) {
      if (t !== 'map') continue;
      const obj = o as ObjID;
      const fragmentHeads: Record<string, string> = {};
      const hm = r.map(obj, 'fragmentHeads');
      if (hm) for (const [fid, [ht, h]] of r.entries(hm)) if (ht === 'str') fragmentHeads[fid] = h as string;
      out.push({
        id, name: r.string(obj, 'name') ?? '', createdAt: r.date(obj, 'createdAt') ?? new Date(DISTANT_PAST),
        createdBy: r.string(obj, 'createdBy') ?? '', trigger: oneOf(TRIGGERS, r.string(obj, 'trigger'), 'manual'),
        epoch: r.int(obj, 'epoch') ?? 0, projectHeads: r.string(obj, 'projectHeads') ?? '', fragmentHeads,
      });
    }
    return out.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }, []);

  const projectType = oneOf(PROJECT_TYPES, setting('projectType', (o) => r.string(o, 'projectType'), 'manuscript'), 'manuscript');
  return {
    id: c.try('id', () => r.requiredString(ROOT, 'id'), ''),
    title: c.try('title', () => r.string(ROOT, 'title') ?? 'Untitled', 'Untitled'),
    createdAt: c.try('createdAt', () => r.date(ROOT, 'createdAt') ?? new Date(DISTANT_PAST), new Date(DISTANT_PAST)),
    source: c.try('source', () => r.string(ROOT, 'source'), undefined),
    epoch: c.try('epoch', () => r.int(ROOT, 'epoch') ?? 0, 0),
    locale: setting('locale', (o) => r.string(o, 'locale'), 'en-AU'),
    projectType,
    scriptFormat: projectType === 'screenplay' ? setting<ScriptFormat>('scriptFormat', (o) => r.string(o, 'scriptFormat'), 'screen') : 'screen',
    series: setting<string | null>('series', (o) => { const s = r.string(o, 'series'); return s === undefined ? undefined : normalizedSeries(s); }, null),
    targets, settings, fragments, fields, arrangements, entities, checkpoints,
    issues: c.issues,
  };
}

/** `decodeJSON`: a settings value stored as JSON; null when absent, empty or unreadable. */
export function settingJSON<T = unknown>(project: ProjectRecord, key: string): T | null {
  const raw = project.settings[key];
  if (typeof raw !== 'string' || raw === '') return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
